/**
 * StepView's app-header integration: title, the four header-action listeners
 * and which actions are shown (moved verbatim from StepView.vue).
 *
 * Registers, IN THIS ORDER, when called:
 *  1. useEvents()            → onUnmounted: unsubscribe the listeners below
 *  2. useHeaderActions(...)  → onMounted: title + ['action:instruction'];
 *                              onUnmounted: clear title/actions
 *  3. watch stepName         → headerActions.setTitle
 *  4. listen action:reset / action:instruction / action:help / action:interim
 *  5. watchEffect            → add/remove action:reset, action:interim,
 *                              action:help
 * Call it LAST of the view-session composables: its watchEffect must run after
 * the step:complete emit watcher (useStepViewLifecycle) and the end-results
 * watchEffect (useStepDialogs). In StepView (before R3) steps 1–4 were set up
 * at the top of the script; they only subscribe/schedule and their timing is
 * unchanged by moving them after the other composables' watchers.
 */
import { computed, toValue, watch, watchEffect } from 'vue';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { useEvents } from 'src/shared/composables/useEvents/useEvents';
import { useHeaderActions } from 'src/shared/composables/useHeaderActions/useHeaderActions';

type ReadRef<T> = Readonly<Ref<T>>;

export interface UseStepHeaderActionsOptions {
  stepName: ReadRef<string>;
  stepDocId: string;
  nextSectionStepDocId: MaybeRefOrGetter<string | undefined>;
  stepTypeCamelCase: ReadRef<string | null | undefined>;
  currentStepResults: ReadRef<Record<string, unknown> | null | undefined>;
  isCompletedStep: ReadRef<boolean>;
  stepDrillProps: ReadRef<
    | { drillSettings?: { show_interim_results?: boolean | null } | null }
    | undefined
  >;
  onReset: () => Promise<unknown>;
  onInstruction: () => void;
  onHelp: () => void;
  onInterim: () => void;
}

export interface UseStepHeaderActionsReturn {
  isShowViewResults: ComputedRef<boolean>;
  isShowResetStep: ComputedRef<boolean>;
}

export function useStepHeaderActions(
  opts: UseStepHeaderActionsOptions,
): UseStepHeaderActionsReturn {
  const { stepName, stepDocId, stepTypeCamelCase, currentStepResults } = opts;
  const { isCompletedStep, stepDrillProps } = opts;

  const { listen } = useEvents();

  const headerActions = useHeaderActions({
    eventActions: ['action:instruction'],
    title: stepName.value || '',
  });

  watch(
    () => stepName.value,
    (newVal) => {
      headerActions.setTitle(newVal);
    },
  );

  // Listen for header actions
  listen('action:reset', () => {
    void opts.onReset();
  });
  listen('action:instruction', () => opts.onInstruction());
  listen('action:help', () => opts.onHelp());
  listen('action:interim', () => opts.onInterim());

  // D-4: the interim-results action only when the drill allows it.
  const isShowViewResults = computed(() => {
    return (
      !!stepDrillProps.value?.drillSettings?.show_interim_results &&
      stepTypeCamelCase.value !== 'Theory' &&
      !!currentStepResults.value &&
      Object.keys(currentStepResults.value).length > 0
    );
  });

  const isShowResetStep = computed(() => {
    return (
      toValue(opts.nextSectionStepDocId) !== stepDocId &&
      stepTypeCamelCase.value !== 'Theory' &&
      !!currentStepResults.value &&
      Object.keys(currentStepResults.value).length > 0
    );
  });

  watchEffect(() => {
    if (!stepTypeCamelCase.value) return;
    // Handle reset action visibility
    if (isShowResetStep.value) {
      headerActions.addAction('action:reset');
    } else {
      headerActions.removeAction('action:reset');
    }

    // Handle interim results action visibility
    if (isShowViewResults.value) {
      headerActions.addAction('action:interim');
    } else {
      headerActions.removeAction('action:interim');
    }

    // Handle help action visibility
    if (isCompletedStep.value) {
      headerActions.removeAction('action:help');
    } else {
      headerActions.addAction('action:help');
    }
  });

  return { isShowViewResults, isShowResetStep };
}
